import { useState } from "react";

type Category = "core" | "advanced" | "instructor";

interface PriceItem {
  name: string;
  price: string;
  badge?: string;
  duration: string;
  features: string[];
  highlight?: boolean;
  bestValue?: boolean;
}

const coreItems: PriceItem[] = [
  {
    name: "BLS Provider Class",
    price: "$85",
    duration: "~4 hours",
    features: [
      "Hands-on RN-led training",
      "Adult, child & infant CPR",
      "AED operation & airway management",
      "Same-day AHA certification",
    ],
  },
  {
    name: "BLS Renewal",
    price: "$75",
    duration: "~2–3 hours",
    features: [
      "Skills review & competency check",
      "Updated AHA protocols",
      "New certification card issued",
      "Streamlined fast process",
    ],
  },
  {
    name: "BLS Blended (Online + Hands-On)",
    price: "$95",
    duration: "Flexible",
    features: [
      "Complete online portion anytime",
      "In-person skills check session",
      "Same-day certification at skills check",
      "Best for busy schedules",
    ],
    highlight: true,
    badge: "Flexible",
  },
  {
    name: "BLS Heartcode Skills Session",
    price: "$55",
    duration: "~1–2 hours",
    features: [
      "For students who completed Heartcode online",
      "Hands-on evaluation only",
      "Fast & efficient certification",
      "AHA-aligned credential",
    ],
  },
  {
    name: "Heartsaver CPR / AED / First Aid",
    price: "$95–$105",
    duration: "~3–4 hours",
    features: [
      "Full CPR, AED & First Aid coverage",
      "Adult, child & infant CPR",
      "Hands-on AED practice",
      "Same-day certification",
    ],
  },
];

const advancedItems: PriceItem[] = [
  {
    name: "ACLS Provider (Initial)",
    price: "$230",
    duration: "1–2 days",
    features: [
      "Advanced airway management",
      "Rhythm recognition & pharmacology",
      "Megacode simulations",
      "Real-life emergency scenarios",
      "Same-day certification",
    ],
    highlight: true,
    badge: "Professional",
  },
  {
    name: "ACLS Renewal / Refresher",
    price: "$175",
    duration: "~4–6 hours",
    features: [
      "Updated ACLS protocols",
      "Hands-on skills practice",
      "Efficient recertification",
      "AHA-aligned credential",
    ],
  },
  {
    name: "PALS Provider (Initial)",
    price: "$230",
    duration: "1–2 days",
    features: [
      "Pediatric assessment & intervention",
      "Respiratory and cardiac scenarios",
      "Hands-on simulations",
      "Same-day certification",
    ],
    highlight: true,
    badge: "Pediatric",
  },
  {
    name: "PALS Renewal / Refresher",
    price: "$175",
    duration: "~4–6 hours",
    features: [
      "Updated pediatric emergency protocols",
      "Skills competency review",
      "Efficient recertification",
      "AHA-aligned credential",
    ],
  },
  {
    name: "ACLS + BLS Combo",
    price: "$250",
    duration: "1–2 days",
    features: [
      "Dual certification in one visit",
      "Streamlined scheduling",
      "Save vs. individual pricing",
      "Same-day certification",
    ],
    badge: "Bundle",
    highlight: true,
  },
  {
    name: "ACLS + PALS + BLS Bundle",
    price: "$425",
    duration: "1–2 days",
    features: [
      "All three certifications included",
      "Hands-on RN-led training",
      "Same-day certification",
      "Streamlined scheduling for efficiency",
    ],
    bestValue: true,
    badge: "Best Value",
  },
];

const instructorItems: PriceItem[] = [
  {
    name: "BLS Instructor Course",
    price: "$500",
    duration: "1 day",
    features: [
      "Step-by-step certification guidance",
      "Teaching methodology & course structure",
      "Hands-on teaching practice",
      "AHA-aligned material guidance",
      "Ongoing mentorship program included",
    ],
    highlight: true,
    badge: "Premium",
  },
];

const TABS: { key: Category; label: string; icon: string }[] = [
  { key: "core", label: "Core BLS & CPR", icon: "ri-heart-pulse-line" },
  { key: "advanced", label: "Advanced (ACLS / PALS)", icon: "ri-stethoscope-line" },
  { key: "instructor", label: "Instructor Program", icon: "ri-user-star-line" },
];

function PriceCard({ item }: { item: PriceItem }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="rounded-xl overflow-hidden transition-all duration-300"
      style={{
        background: item.bestValue
          ? "linear-gradient(135deg, rgba(212,175,55,0.14), rgba(107,70,193,0.18))"
          : item.highlight
          ? "rgba(107,70,193,0.1)"
          : "rgba(255,255,255,0.04)",
        border: item.bestValue
          ? "1.5px solid rgba(212,175,55,0.7)"
          : item.highlight
          ? "1px solid rgba(107,70,193,0.45)"
          : "1px solid rgba(255,255,255,0.08)",
      }}
    >
      {item.bestValue && (
        <div
          className="text-center py-1.5 text-xs font-black uppercase tracking-widest"
          style={{ background: "linear-gradient(90deg, #D4AF37, #b8952e)", color: "#1a1a1a" }}
        >
          ⭐ Best Value — Most Popular Bundle
        </div>
      )}
      <div className="px-5 py-4">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <h3 className="text-white font-bold text-sm leading-snug">{item.name}</h3>
              {item.badge && !item.bestValue && (
                <span
                  className="text-xs font-bold px-2 py-0.5 rounded-full whitespace-nowrap"
                  style={{
                    background: "rgba(107,70,193,0.3)",
                    border: "1px solid rgba(107,70,193,0.5)",
                    color: "#c4b5fd",
                  }}
                >
                  {item.badge}
                </span>
              )}
            </div>
            <p className="text-white/40 text-xs flex items-center gap-1">
              <i className="ri-time-line" />
              {item.duration}
            </p>
          </div>
          <div className="text-right shrink-0">
            <p
              className="font-serif font-bold text-xl"
              style={{
                color: item.bestValue ? "#D4AF37" : item.highlight ? "#d4af37" : "rgba(255,255,255,0.9)",
              }}
            >
              {item.price}
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={() => setOpen(!open)}
          className="flex items-center gap-1.5 text-xs font-semibold cursor-pointer transition-colors"
          style={{ color: "rgba(212,175,55,0.8)" }}
        >
          <i className={`${open ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}`} />
          {open ? "Hide details" : "What&apos;s included"}
        </button>

        <div
          className="overflow-hidden transition-all duration-400"
          style={{ maxHeight: open ? "200px" : "0px", opacity: open ? 1 : 0 }}
        >
          <ul className="mt-3 space-y-1.5">
            {item.features.map((f) => (
              <li key={f} className="flex items-start gap-2 text-xs text-white/65 leading-relaxed">
                <i className="ri-check-line text-brand-gold mt-0.5 flex-shrink-0" />
                {f}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

export default function PricingPanel() {
  const [activeTab, setActiveTab] = useState<Category>("core");

  const items: Record<Category, PriceItem[]> = {
    core: coreItems,
    advanced: advancedItems,
    instructor: instructorItems,
  };

  return (
    <div className="flex flex-col gap-6 h-full">
      {/* Category Tabs */}
      <div
        className="flex gap-1 p-1 rounded-xl"
        style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)" }}
      >
        {TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setActiveTab(tab.key)}
            className="flex-1 flex items-center justify-center gap-1.5 py-2.5 px-2 rounded-lg text-xs font-bold transition-all duration-300 cursor-pointer whitespace-nowrap"
            style={
              activeTab === tab.key
                ? { background: "linear-gradient(135deg, #6B46C1, #4C1D95)", color: "#fff", boxShadow: "0 2px 12px rgba(107,70,193,0.4)" }
                : { color: "rgba(255,255,255,0.45)" }
            }
          >
            <i className={`${tab.icon} text-sm`} />
            <span className="hidden sm:inline">{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Cards */}
      <div className="flex flex-col gap-3 overflow-y-auto pr-1" style={{ maxHeight: "520px" }}>
        {items[activeTab].map((item) => (
          <PriceCard key={item.name} item={item} />
        ))}

        {/* Mentorship callout (only on instructor tab) */}
        {activeTab === "instructor" && (
          <div
            className="rounded-xl px-5 py-5 mt-2"
            style={{
              background: "linear-gradient(135deg, rgba(212,175,55,0.1), rgba(107,70,193,0.15))",
              border: "1.5px solid rgba(212,175,55,0.4)",
            }}
          >
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 flex items-center justify-center rounded-lg" style={{ background: "rgba(212,175,55,0.2)" }}>
                <i className="ri-award-line text-brand-gold text-base" />
              </div>
              <div>
                <p className="text-white font-bold text-sm">Instructor Mentorship Program</p>
                <p className="text-brand-gold text-xs font-semibold">Included with Instructor Course</p>
              </div>
            </div>
            <p className="text-white/60 text-xs leading-relaxed mb-3 italic">
              &ldquo;Not just certification — we help you build.&rdquo;
            </p>
            <ul className="space-y-1.5">
              {[
                "Guidance on starting your own CPR training business",
                "Pricing strategies and class structuring",
                "Scheduling system setup support",
                "Ongoing mentorship from RN instructors",
                "Real-world coaching and confidence building",
              ].map((item) => (
                <li key={item} className="flex items-start gap-2 text-xs text-white/65">
                  <i className="ri-check-line text-brand-gold mt-0.5 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Contact strip */}
      <div
        className="rounded-xl px-5 py-4 mt-auto"
        style={{
          background: "rgba(255,255,255,0.03)",
          border: "1px solid rgba(212,175,55,0.2)",
        }}
      >
        <p className="text-white/50 text-xs mb-3 text-center">
          Need help choosing? Call or text us now.
        </p>
        <div className="flex flex-col gap-2">
          <a
            href="tel:4075643966"
            className="flex items-center gap-3 px-4 py-2.5 rounded-lg cursor-pointer transition-all duration-200 hover:opacity-80"
            style={{ background: "rgba(107,70,193,0.25)", border: "1px solid rgba(107,70,193,0.4)" }}
          >
            <div className="w-7 h-7 flex items-center justify-center rounded-full" style={{ background: "rgba(107,70,193,0.5)" }}>
              <i className="ri-phone-line text-brand-gold text-xs" />
            </div>
            <div>
              <p className="text-white text-xs font-bold leading-none">Cardiocraft CPR</p>
              <p className="text-brand-gold text-xs font-semibold">407-564-3966</p>
            </div>
            <i className="ri-arrow-right-line text-white/30 text-xs ml-auto" />
          </a>
          <a
            href="tel:9172856715"
            className="flex items-center gap-3 px-4 py-2.5 rounded-lg cursor-pointer transition-all duration-200 hover:opacity-80"
            style={{ background: "rgba(212,175,55,0.08)", border: "1px solid rgba(212,175,55,0.25)" }}
          >
            <div className="w-7 h-7 flex items-center justify-center rounded-full" style={{ background: "rgba(212,175,55,0.2)" }}>
              <i className="ri-phone-line text-brand-gold text-xs" />
            </div>
            <div>
              <p className="text-white text-xs font-bold leading-none">Nathaniel (Direct)</p>
              <p className="text-brand-gold text-xs font-semibold">917-285-6715</p>
            </div>
            <i className="ri-arrow-right-line text-white/30 text-xs ml-auto" />
          </a>
        </div>
      </div>
    </div>
  );
}
